import { Injectable } from '@angular/core';
import { ElfBaseStore } from '../../store/base-store.repository';
import { Subject } from 'rxjs';
import { string } from '@/utils/string.utils';

export type FormNode = BranchNode | LeafNode;

export interface BranchNode {
  children: FormNode[];
}

export interface LeafNode {
  store?: ElfBaseStore<any>;
}

@Injectable({ providedIn: 'root' })
export class MultiFormAccordionService {
  //emit new stores
  newStore$ = new Subject<ElfBaseStore<any> | undefined>();
  storeRemoved$ = new Subject<ElfBaseStore<any>>();

  static openPanelIfClosed(panelId: string, scroll = true) {
    const panel = document.querySelector(`[data-panel-id="${panelId}"]`);
    if (!panel) return;

    if (panel.classList.contains('p-accordionpanel-active')) {
      return;
    }

    const header = panel.querySelector('.p-accordion-header');
    if (header instanceof HTMLElement) {
      if (scroll) {
        panel.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      header.click();
    }
  }
  _formFactory?: (id: string) => ElfBaseStore<any>;
  root: FormNode = { children: [] };

  set formFactory(factory: (id: string) => ElfBaseStore<any>) {
    this._formFactory = factory;
  }

  init(depth = 1): void {
    this.root = this.createTree(depth + 1);
  }

  initStructure(structure: FormNode): void {
    const assignStores = (node: FormNode): FormNode => {
      if (isLeafNode(node)) {
        const store = this._formFactory?.(string.generateUniqueId());
        this.newStore$.next(store);
        return { store };
      }

      if (isBranchNode(node)) {
        return {
          children: node.children.map(child => assignStores(child)),
        };
      }

      throw new Error('Invalid node type');
    };

    this.root = assignStores(structure);
  }

  private createTree(depth: number): FormNode {
    if (depth <= 1) {
      const store = this._formFactory?.(string.generateUniqueId());
      this.newStore$.next(store);
      return { store };
    }
    return { children: [this.createTree(depth - 1)] };
  }

  add(path: number[] = []): void {
    const node = this.getNodeAt(path);

    if (!isBranchNode(node)) {
      throw new Error('Cannot add to a leaf node');
    }

    const remainingDepth = this.getDepth() - path.length;
    const newChild = this.createTree(remainingDepth - 1);

    node.children.push(newChild);
  }

  getDepth(): number {
    let depth = 0;
    let node = this.root;
    while (isBranchNode(node) && node.children.length > 0) {
      depth++;
      node = node.children[0];
    }
    return isLeafNode(node) ? depth + 1 : depth;
  }

  remove(path: number[]): void {
    if (path.length === 0) return;

    const indexToRemove = path[path.length - 1];
    const parentPath = path.slice(0, -1);
    const parentNode = this.getNodeAt(parentPath);

    if (!isBranchNode(parentNode)) {
      throw new Error('Cannot remove from a leaf node');
    }

    const removed = parentNode.children.splice(indexToRemove, 1)[0];

    if (isLeafNode(removed) && removed.store) {
      this.storeRemoved$.next(removed.store);
    }
  }
  getNodeAt(path: number[]): FormNode {
    let node: FormNode = this.root;

    for (const index of path) {
      if (!isBranchNode(node)) {
        throw new Error(`Invalid path: reached leaf at index ${index}`);
      }

      if (!node.children[index]) {
        throw new Error(`Invalid path: no child at index ${index}`);
      }

      node = node.children[index];
    }

    return node;
  }

  getAllStores(): ElfBaseStore<any>[] {
    return this.collectStores(this.root);
  }

  private collectStores(node: FormNode): ElfBaseStore<any>[] {
    if (isLeafNode(node) && node.store) {
      return [node.store];
    }

    if (isBranchNode(node)) {
      return node.children.flatMap(child => this.collectStores(child));
    }

    return [];
  }

  /** Checks if a node is a leaf node (has a FormControl) */
  isLeaf(node: FormNode): node is LeafNode {
    return isLeafNode(node);
  }

  /** Checks if a node is a branch (has children) */
  isBranch(node: FormNode): node is BranchNode {
    return isBranchNode(node);
  }

  reset(initDepth = 1): void {
    if (initDepth === 0) {
      this.initStructure({
        children: [],
      });
    } else {
      this.getAllStores().forEach(store => {
        if ('destroy' in store && typeof store.destroy === 'function') {
          store.destroy();
        }
      });

      this.root = { children: [] };

      this.init(initDepth);
    }
  }
}

/** Type guards */
function isLeafNode(node: FormNode): node is LeafNode {
  return 'store' in node;
}

function isBranchNode(node: FormNode): node is BranchNode {
  return 'children' in node;
}
